import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "../components/layout/Layout";
import api, { waLink, WHATSAPP_DISPLAY, WHATSAPP_NUMBER, CONTACT_EMAIL } from "../lib/api";
import useSeo from "../hooks/useSeo";
import { ChevronRight, Phone, Mail, MessageCircle, MapPin } from "lucide-react";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Button } from "../components/ui/button";
import { toast } from "sonner";

export default function Contact() {
  useSeo({ title: "İletişim — Otel Pro", description: "Otel projeniz için uzman danışmanlığımıza 24 saat içinde ulaşın." });
  const [form, setForm] = useState({
    contact_name: "", contact_email: "", contact_phone: "", company: "",
    facility_type: "Otel", star_target: "4", room_count: 0, notes: "",
  });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/quotes", { ...form, room_count: Number(form.room_count || 0), items: [], source: "contact" });
      toast.success("Talebiniz alındı. 24 saat içinde dönüş yapacağız.");
      setForm({ contact_name: "", contact_email: "", contact_phone: "", company: "", facility_type: "Otel", star_target: "4", room_count: 0, notes: "" });
    } catch { toast.error("Gönderim başarısız."); }
    finally { setLoading(false); }
  };

  return (
    <Layout>
      <div className="bg-softbg">
        <div className="container-site py-5 flex items-center gap-2 text-xs text-gray-500">
          <Link to="/" className="hover:text-navy">Ana Sayfa</Link>
          <ChevronRight size={12} />
          <span className="text-navy font-medium">İletişim</span>
        </div>
      </div>

      <section className="bg-navy text-white py-20">
        <div className="container-site max-w-3xl">
          <p className="accent-script text-gold text-2xl mb-2">Yanınızdayız</p>
          <h1 className="heading-serif text-3xl md:text-5xl font-semibold">Otel Projeniz İçin Danışmanlık</h1>
          <p className="text-white/75 mt-4">
            Uzman danışmanlarımız projenize özel ekipman listesi hazırlar, yıldız uyumluluğu kontrolü yapar ve 24 saat içinde özel teklif oluşturur.
          </p>
        </div>
      </section>

      <section className="section-pad">
        <div className="container-site max-w-5xl grid md:grid-cols-3 gap-8">
          <div className="md:col-span-1 space-y-4">
            <ContactCard icon={Phone} label="Telefon" value={WHATSAPP_DISPLAY} href={`tel:+${WHATSAPP_NUMBER}`} testid="contact-phone" />
            <ContactCard icon={Mail} label="E-posta" value={CONTACT_EMAIL} href={`mailto:${CONTACT_EMAIL}`} testid="contact-email" />
            <ContactCard icon={MessageCircle} label="WhatsApp" value="Hızlı yanıt" href={waLink("Merhaba, otel projem için danışmanlık almak istiyorum.")} testid="contact-whatsapp" />
            <ContactCard icon={MapPin} label="Hizmet Alanı" value="Türkiye Geneli" testid="contact-area" />
          </div>
          <form onSubmit={submit} className="md:col-span-2 card-premium p-7 space-y-4" data-testid="contact-form">
            <h2 className="heading-serif text-2xl font-semibold text-navy mb-2">Hızlı Teklif Formu</h2>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Ad Soyad *" value={form.contact_name} onChange={(v) => setForm({ ...form, contact_name: v })} testid="contact-name" />
              <Field label="Firma" value={form.company} onChange={(v) => setForm({ ...form, company: v })} testid="contact-company" />
              <Field label="E-posta *" type="email" value={form.contact_email} onChange={(v) => setForm({ ...form, contact_email: v })} testid="contact-form-email" />
              <Field label="Telefon" value={form.contact_phone} onChange={(v) => setForm({ ...form, contact_phone: v })} testid="contact-form-phone" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label className="text-xs mb-1 block">Tesis Türü</Label>
                <select className="w-full border rounded-md h-10 px-3 bg-softbg text-sm" value={form.facility_type} onChange={(e) => setForm({ ...form, facility_type: e.target.value })} data-testid="contact-facility">
                  <option>Otel</option><option>Butik Otel</option><option>Resort</option><option>Apart</option><option>Pansiyon</option>
                </select>
              </div>
              <div>
                <Label className="text-xs mb-1 block">Yıldız Hedefi</Label>
                <select className="w-full border rounded-md h-10 px-3 bg-softbg text-sm" value={form.star_target} onChange={(e) => setForm({ ...form, star_target: e.target.value })} data-testid="contact-star">
                  {[1,2,3,4,5].map((s) => <option key={s} value={s}>{s} Yıldız</option>)}
                </select>
              </div>
              <Field label="Oda Sayısı" type="number" value={form.room_count} onChange={(v) => setForm({ ...form, room_count: v })} testid="contact-room-count" />
            </div>
            <div>
              <Label className="text-xs mb-1 block">Projeniz hakkında notlar</Label>
              <Textarea rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="contact-notes" />
            </div>
            <Button type="submit" disabled={loading} className="bg-gold hover:bg-[#c49d58]" data-testid="contact-submit">
              {loading ? "Gönderiliyor..." : "Ücretsiz Danışmanlık Al"}
            </Button>
          </form>
        </div>
      </section>
    </Layout>
  );
}

const ContactCard = ({ icon: Icon, label, value, href, testid }) => {
  const content = (
    <div className="card-premium p-5 flex items-start gap-3">
      <div className="h-10 w-10 rounded-md bg-gold/10 text-gold flex items-center justify-center shrink-0">
        <Icon size={18} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-wider text-gray-500">{label}</div>
        <div className="text-navy font-medium text-sm mt-0.5">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} data-testid={testid}>{content}</a> : <div data-testid={testid}>{content}</div>;
};

const Field = ({ label, type = "text", value, onChange, testid }) => (
  <div>
    <Label className="text-xs mb-1 block">{label}</Label>
    <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
  </div>
);
